import React from 'react';
import type {Collection} from '@shopify/hydrogen/storefront-api-types';
import {
  useLoaderData,
  useParams,
  useLocation,
  useMatches,
} from '@remix-run/react';
import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/dist/ScrollTrigger';
import {useIsHydrated} from '~/hooks/useIsHydrated';

//domains
import {loader} from '~/routes/($lang)/collections/$collectionHandle';
import {STOCK_STATUS_FILTER_ID} from 'domains/nativeCollection';
import {MODEL_FACET_ID, type BrandModel} from '~/lib/brandModels';
import type {I18nLocale} from '~/lib/type';

//components
import CollectionBreadcrumb from '~/components/collection/CollectionBreadcrumb';
import CollectionSorting from 'components/collection/CollectionSorting';
import CollectionProducts from 'components/collection/CollectionProducts';
import CollectionPagination from 'components/collection/CollectionPagination';
import NativeCollectionFilter from '~/components/collection/CollectionFilter/NativeCollectionFilter';
import CollectionMobileFilterAndSort from './CollectionMobileFilterAndSort';
import BrandBreadcrumb from './BrandBreadcrumb';
import BrandModelBand from './BrandModelBand';

interface Props {
  type?: string;
}

export default function CollectionMain({type}: Props) {
  const loaderData = useLoaderData<typeof loader>();
  const {totalProducts, translation, collection} = loaderData;
  // Only brand pages ship a selected 型號: the vendor route and a brand's
  // own collection (which the vendor route now redirects to).
  const brandModel =
    (loaderData as {brandModel?: BrandModel | null}).brandModel ?? null;
  const isBrandCollection = Boolean(
    (loaderData as {brandVendor?: string | null}).brandVendor,
  );
  const isBrandPage = type === 'brands' || isBrandCollection;
  const [root] = useMatches();
  const isZhLocale =
    (root.data?.selectedLocale as I18nLocale | undefined)?.language !== 'EN';
  const {search} = useLocation();
  const filterColRef = React.useRef<HTMLDivElement>(null);

  // The PLP had no <h1>. Give it one (the collection / brand name) for on-page
  // SEO + accessibility. Kept visually-hidden so the existing layout (which
  // already shows the name in the breadcrumb) is untouched. On brand pages the
  // page IS the vendor, taken from the pf_v_brand identity param.
  const pageHeading =
    type === 'brands'
      ? new URLSearchParams(search).get('pf_v_brand') ?? ''
      : (collection as {title?: string} | undefined)?.title ?? '';
  const brandName = isBrandCollection
    ? (collection as {title?: string} | undefined)?.title
    : undefined;
  const mainRef = React.useRef<HTMLDivElement>(null);
  const sortingRef = React.useRef<HTMLDivElement>(null);

  // Facets to omit from the sidebar. Brand pages drop the vendor facet (the
  // brand IS the page); the 特賣場 (close-out / clearance) and 預訂產品
  // (preorder-products) collections drop the 供貨情況 (stock_status) facet —
  // preorder items are always out of stock, so the facet is meaningless there.
  const {collectionHandle} = useParams();
  const hideFacets = React.useMemo(() => {
    const hidden: string[] = [];
    if (isBrandPage) hidden.push('filter.p.vendor');
    // 型號 is a per-brand axis. Shopify surfaces the custom.model facet on
    // every collection that happens to contain a product carrying the
    // metafield, where one brand's model list would sit above 產品類型 and
    // 品牌 while describing a fraction of the page. Brand pages only.
    if (!isBrandPage) hidden.push(MODEL_FACET_ID);
    if (
      collectionHandle === 'close-out-products' ||
      collectionHandle === 'preorder-products'
    ) {
      hidden.push(STOCK_STATUS_FILTER_ID);
    }
    return hidden.length ? hidden : undefined;
  }, [isBrandPage, collectionHandle]);

  //   React.useEffect(() => {
  //     ScrollTrigger.matchMedia({
  //       '(min-width: 1024px)': function () {
  //         if (filterColRef?.current && mainRef?.current) {
  //           gsap.timeline({
  //             scrollTrigger: {
  //               start: 'top 217px',
  //               trigger: filterColRef?.current,
  //               endTrigger: '#email-signup',
  //               end: 'top bottom',
  //               pin: true,
  //               pinSpacing: false,
  //             },
  //           });
  //         }
  //         if (sortingRef?.current && mainRef?.current) {
  //           gsap.timeline({
  //             scrollTrigger: {
  //               start: 'top 181px',
  //               trigger: sortingRef?.current,
  //               endTrigger: '#email-signup',
  //               end: 'top bottom',
  //               pin: true,
  //               pinSpacing: false,
  //             },
  //           });
  //         }
  //       },
  //     });
  //   }, []);
  const isHydrated = useIsHydrated();

  return (
    <div className="grid lg:grid-cols-8 lg:gap-10 lg:pt-10" ref={mainRef}>
      {pageHeading && <h1 className="sr-only">{pageHeading}</h1>}
      <div className="relative lg:col-span-2 hidden lg:block">
        <div className="relative lg:col-span-2">
          <div className="" ref={filterColRef}>
            <div className="lg:pr-4 lg:pb-64">
              {isBrandPage ? (
                <BrandBreadcrumb name={brandName} />
              ) : (
                <CollectionBreadcrumb />
              )}
              <div className="hidden lg:block">
                <NativeCollectionFilter hideFacets={hideFacets} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="lg:col-span-6">
        {brandModel && (
          <BrandModelBand model={brandModel} isZhLocale={isZhLocale} />
        )}
        <div
          ref={sortingRef}
          className="relative z-[30] md:z-10 md:mt-4 flex flex-col md:flex-row w-full items-center justify-between bg-white px-5 mt-0 pt-1 pb-1 md:py-2 lg:relative lg:left-0 lg:mt-0 lg:mb-14 lg:h-14 lg:w-full lg:bg-hk-orange lg:px-4 mobile-collections-bar"
        >
          <div className="w-full lg:hidden">
            {isBrandPage ? (
              <BrandBreadcrumb name={brandName} />
            ) : (
              <CollectionBreadcrumb />
            )}
          </div>
          <div className="text-sm w-full lg:w-auto">
            {translation.product_listing ?? `Product Listing`}: {totalProducts}
          </div>
          <div className="hidden lg:block">
            <CollectionSorting />
          </div>
        </div>
        <div className="relative mt-2 w-full px-5 lg:hidden collection-mobile-filter-sort z-[30]">
          {isHydrated && (
            <CollectionMobileFilterAndSort hideFacets={hideFacets} />
          )}
        </div>
        {/* Category intro copy authored in Shopify (collection.descriptionHtml,
            zh-TW via translations) — buying-guide context for shoppers and the
            crawlable text AI/search engines summarize. Server-rendered; brand
            pages have no collection so they never show it. */}
        {type !== 'brands' &&
        (collection as {descriptionHtml?: string} | undefined)
          ?.descriptionHtml ? (
          <div
            className="collection-intro mb-6 mt-4 px-5 text-sm leading-relaxed text-darkGray lg:mt-0 lg:px-0"
            dangerouslySetInnerHTML={{
              __html: (collection as {descriptionHtml: string}).descriptionHtml,
            }}
          />
        ) : null}
        <CollectionProducts />
        <CollectionPagination />
      </div>
    </div>
  );
}
